import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

// Error message display duration - gives users time to read the message
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts
const ERROR_BUTTON_REENABLE_DELAY = 1000;

// Success message display duration for screen readers
const SUCCESS_MESSAGE_DISPLAY_DURATION = 5000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if adding would exceed max before animating
    const quantitySelector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();
      // Don't animate if it would exceed max
      if (!validation.canAdd) {
        return;
      }
    }

    // Check if product has agreements - if so, skip animation here (will be triggered after dialog confirmation)
    const productFormComponent = /** @type {HTMLElement | null} */ (this.closest('product-form-component'));
    const hasAgreements = productFormComponent?.dataset.agreements;
    if (hasAgreements) {
      // Skip animation - it will be triggered after agreement dialog confirmation
      return;
    }

    // this.animateAddToCart();

    // const animationEnabled = this.dataset.addToCartAnimation === 'true';

    // if (animationEnabled && !event.target.closest('.quick-add-modal')) {
    //   this.#animateFlyToCart();
    // }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Public method to trigger all add to cart animations.
   * Used after agreement dialog confirmation.
   */
  triggerAnimations() {
    // this.animateAddToCart();

    // const animationEnabled = this.dataset.addToCartAnimation === 'true';
    // if (animationEnabled) {
    //   this.#animateFlyToCart();
    // }
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} quantityLabelCartCount - The quantity label cart count element.
 * @property {HTMLElement | undefined} quantityRules - The quantity rules element.
 * @property {HTMLElement | undefined} productFormButtons - The product form buttons container.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  // One-shot bypass for the retriggered accelerated checkout click.
  #skipNextAcceleratedCheckoutClick = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });

    // Listen for cart updates to sync data-cart-quantity
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches cart and updates quantity selector for current variant
   * @returns {Promise<number>} The cart quantity for the current variant
   */
  async #fetchAndUpdateCartQuantity() {
    const variantIdInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="id"]'));
    if (!variantIdInput?.value) return 0;

    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const cartItem = cart.items.find(
        /** @param {any} item */
        (item) => item.variant_id.toString() === variantIdInput.value.toString()
      );
      const cartQty = cartItem ? cartItem.quantity : 0;

      // Use public API to update quantity selector
      const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
      if (quantitySelector?.setCartQuantity) {
        quantitySelector.setCartQuantity(cartQty);
      }

      // Update quantity label if it exists
      this.#updateQuantityLabel(cartQty);

      return cartQty;
    } catch (error) {
      console.error('Failed to fetch cart quantity:', error);
      return 0;
    }
  }

  /**
   * Updates data-cart-quantity when cart is updated from elsewhere
   * @param {CartUpdateEvent|CartAddEvent} event
   */
  #onCartUpdate = async (event) => {
    // Skip if this event came from this component
    if (event.detail?.sourceId === this.id || event.detail?.data?.source === 'product-form-component') return;

    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    // BH Simple Wishlist 등 앱 블록이 상품 폼 안에 있을 때 기본 submit 버튼이 장바구니 로직을 태우지 않도록 함
    const submitter =
      event instanceof SubmitEvent && event.submitter instanceof HTMLElement ? event.submitter : null;
    if (submitter?.closest('.product-form-buttons__wishlist-slot')) {
      return;
    }

    // Check if user is logged in - THIS MUST BE FIRST
    if (addToCartButtonContainer?.dataset.customerLoggedIn === 'false') {
      const loginUrl = addToCartButtonContainer.dataset.loginUrl;
      if (loginUrl) {
        window.location.href = loginUrl;
        return;
      }
    }

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (addToCartButtonContainer?.refs.addToCartButton?.disabled) return;

    if (this.#showAgreementDialog(() => {
      this.#proceedWithAddToCart(event);
    })) {
      return;
    }

    // No agreements or dialog not found - proceed normally
    this.#proceedWithAddToCart(event);
  }

  /**
   * Handles clicks on the accelerated checkout button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleAcceleratedCheckoutClick(event) {
    if (this.#skipNextAcceleratedCheckoutClick) {
      this.#skipNextAcceleratedCheckoutClick = false;
      return;
    }

    const checkoutTrigger = this.#getAcceleratedCheckoutTrigger(event);
    if (!checkoutTrigger) return;

    if (this.#showAgreementDialog(() => {
      this.#retryAcceleratedCheckout(checkoutTrigger);
    }, this.#getCheckoutButtonLabel())) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  /**
   * Returns the parsed agreements metafield value.
   * @returns {string[] | null}
   */
  #getAgreements() {
    const agreementsData = this.dataset.agreements;
    if (!agreementsData) return null;

    try {
      const agreements = JSON.parse(agreementsData);
      if (Array.isArray(agreements) && agreements.length > 0) {
        return agreements;
      }
    } catch (e) {
      // If JSON parsing fails, proceed normally
      console.error('Failed to parse agreements data:', e);
    }

    return null;
  }

  /**
   * Returns the translated checkout button label from the agreement dialog element.
   * @returns {string | undefined}
   */
  #getCheckoutButtonLabel() {
    const sectionId = this.dataset.sectionId;
    const dialogId = `agreement-dialog-${sectionId}`;
    const agreementDialog = document.getElementById(dialogId);
    return agreementDialog?.dataset.checkoutButtonLabel || undefined;
  }

  /**
   * Shows the agreement dialog when agreements exist.
   * @param {() => void} callback - Runs after agreement confirmation.
   * @param {string} [confirmButtonLabel] - Optional confirm button label.
   * @returns {boolean}
   */
  #showAgreementDialog(callback, confirmButtonLabel) {
    const agreements = this.#getAgreements();
    if (!agreements) return false;

    const sectionId = this.dataset.sectionId;
    const dialogId = `agreement-dialog-${sectionId}`;
    const agreementDialog = /** @type {any} */ (document.getElementById(dialogId));

    if (agreementDialog?.showWithCallback) {
      agreementDialog.showWithCallback(callback, confirmButtonLabel);
      return true;
    }

    return false;
  }

  /**
   * Finds the actual checkout trigger element for the accelerated checkout button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   * @returns {HTMLElement | null}
   */
  #getAcceleratedCheckoutTrigger(event) {
    const path = event.composedPath?.() ?? [];
    const buttonSelector = 'button.shopify-payment-button__button, button.shopify-payment-button__button--unbranded';
    const moreOptionsSelector =
      'more-payment-options-link, button.shopify-payment-button__more-options, a.shopify-payment-button__more-options';
    const hostSelector = 'shopify-accelerated-checkout, shopify-accelerated-checkout-cart, shopify-payment-button';

    // Only gate the primary buy-now button; leave the "more payment options" link alone.
    if (path.some((node) => node instanceof Element && node.matches(moreOptionsSelector))) {
      return null;
    }

    for (const node of path) {
      if (!(node instanceof HTMLElement)) continue;

      if (node.matches(buttonSelector)) {
        return node;
      }

      if (node.matches(hostSelector)) {
        const nestedButton = node.querySelector(buttonSelector);
        if (nestedButton instanceof HTMLElement) {
          return nestedButton;
        }

        return node;
      }
    }

    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) return null;

    const nestedButton = container.querySelector(buttonSelector);
    if (nestedButton instanceof HTMLElement) {
      return nestedButton;
    }

    return /** @type {HTMLElement | null} */ (container.querySelector(hostSelector));
  }

  /**
   * Re-triggers the accelerated checkout button after agreement confirmation.
   * @param {HTMLElement} trigger - The checkout trigger element.
   */
  #retryAcceleratedCheckout(trigger) {
    this.#skipNextAcceleratedCheckoutClick = true;

    try {
      trigger.click();
    } finally {
      if (this.#skipNextAcceleratedCheckoutClick) {
        this.#skipNextAcceleratedCheckoutClick = false;
      }
    }
  }

  /**
   * Proceeds with adding item to cart (after agreement confirmation if needed).
   *
   * @param {Event} event - The original submit event.
   */
  #proceedWithAddToCart(event) {
    const { addToCartTextError, addToCartButtonContainer } = this.refs;

    // Trigger animation for agreement dialog flow (animation was skipped in handleClick)
    if (this.dataset.agreements && addToCartButtonContainer) {
      addToCartButtonContainer.triggerAnimations();
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    if (quantitySelector?.canAddToCart) {
      const validation = quantitySelector.canAddToCart();

      if (!validation.canAdd) {
        addToCartButtonContainer?.disable();

        const errorTemplate = this.dataset.quantityErrorMax || '';
        const errorMessage = errorTemplate.replace('{{ maximum }}', validation.maxQuantity.toString());
        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = errorMessage;
          } else {
            const newTextNode = document.createTextNode(errorMessage);
            addToCartTextError.appendChild(newTextNode);
          }

          this.#setLiveRegionText(errorMessage);

          if (this.#timeout) clearTimeout(this.#timeout);
          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);
        }

        setTimeout(() => {
          addToCartButtonContainer?.enable();
        }, ERROR_BUTTON_REENABLE_DELAY);

        return;
      }
    }

    const formData = new FormData(form);

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, ERROR_MESSAGE_DISPLAY_DURATION);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, SUCCESS_MESSAGE_DISPLAY_DURATION);
          }

          // Fetch the updated cart to get the actual total quantity for this variant
          this.#fetchAndUpdateCartQuantity();

          // Show the "Added to Cart" dialog
          const addedToCartDialogId = `added-to-cart-dialog-${this.dataset.sectionId}`;
          const addedToCartDialog = /** @type {any} */ (document.getElementById(addedToCartDialogId));
          
          if (addedToCartDialog?.show) {
            const atcDataset = this.refs.addToCartButtonContainer?.dataset;
            const optionLabelColor = atcDataset?.atcOptionLabelColor?.trim() || 'Color';
            const optionLabelSize = atcDataset?.atcOptionLabelSize?.trim() || 'Size';
            const optionLabelQuantity = atcDataset?.atcOptionLabelQuantity?.trim() || 'Qty';
            const optionLabelVariant = atcDataset?.atcOptionLabelVariant?.trim() || 'Option';

            const productData = {
              id: this.dataset.productId,
              title: response.product_title,
              image: response.image,
              quantity: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              quantityLabel: optionLabelQuantity,
              /** @type {Record<string, any>} */
              options: {}
            };

            const productColorChip = this.refs.addToCartButtonContainer?.dataset.productColorChip?.trim();
            if (productColorChip) {
              productData.options[optionLabelColor] = {
                value: productColorChip,
                swatch: productColorChip,
              };
            }

            // Capture variant options with correct labels
            const optionNamesData = this.dataset.optionNames;
            let optionNames = [];
            try {
              optionNames = optionNamesData ? JSON.parse(optionNamesData) : [];
            } catch (e) {
              console.error('Failed to parse option names:', e);
            }

            if (response.variant_options) {
              response.variant_options.forEach(
                /**
                 * @param {any} option
                 * @param {number} index
                 */
                (option, index) => {
                  let label = optionNames[index] || `${optionLabelVariant} ${index + 1}`;
                  const normalizedLabel = label.trim().toLowerCase();

                  if (normalizedLabel === 'size') {
                    label = optionLabelSize;
                  } else if (normalizedLabel === 'color' || normalizedLabel === 'colour') {
                    label = optionLabelColor;
                  }

                  if (productColorChip && label === optionLabelColor) return;
                  
                  // Extract swatch if available in the current variant-picker
                  let swatch = null;
                  const sectionId = this.dataset.sectionId;
                  const fieldset = document.querySelector(`variant-picker[data-section-id="${sectionId}"] fieldset[data-fieldset-index="${index}"]`);
                  if (fieldset) {
                    const checkedInput = fieldset.querySelector('input:checked');
                    if (checkedInput) {
                      const swatchEl = checkedInput.parentElement?.querySelector('.swatch');
                      if (swatchEl) {
                        swatch = getComputedStyle(swatchEl).getPropertyValue('--swatch-background');
                      }
                    }
                  }

                  productData.options[label] = {
                    value: option,
                    swatch: swatch
                  };
                }
              );
            } else if (response.variant_title && response.variant_title !== 'Default Title') {
              productData.options[optionLabelVariant] = { value: response.variant_title };
            }

            addedToCartDialog.show(productData);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * Updates the quantity label with the current cart quantity
   * @param {number} cartQty - The quantity in cart
   */
  #updateQuantityLabel(cartQty) {
    const quantityLabel = this.refs.quantityLabelCartCount;
    if (quantityLabel) {
      const inCartText = quantityLabel.textContent?.match(/\((\d+)\s+(.+)\)/);
      if (inCartText && inCartText[2]) {
        quantityLabel.textContent = `(${cartQty} ${inCartText[2]})`;
      }

      // Show/hide based on quantity
      quantityLabel.classList.toggle('hidden', cartQty === 0);
    }
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Morphs or removes/adds an element based on current and new element states
   * @param {Element | null | undefined} currentElement - The current element in the DOM
   * @param {Element | null | undefined} newElement - The new element from the server response
   * @param {Element | null} [insertReferenceElement] - Element to insert before if adding new element
   */
  #morphOrUpdateElement(currentElement, newElement, insertReferenceElement = null) {
    if (currentElement && newElement) {
      morph(currentElement, newElement);
    } else if (currentElement && !newElement) {
      currentElement.remove();
    } else if (!currentElement && newElement && insertReferenceElement) {
      insertReferenceElement.insertAdjacentElement('beforebegin', /** @type {Element} */ (newElement.cloneNode(true)));
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = async (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    // Update the variant ID
    variantId.value = event.detail.resource?.id ?? '';

    if (!currentAddToCartButton && !this.refs.acceleratedCheckoutButtonContainer) return;

    // Update the button state
    if (currentAddToCartButton) {
      if (event.detail.resource == null || event.detail.resource.available == false) {
        addToCartButtonContainer.disable();
      } else {
        const pvt = this.dataset.preViewTime;
        if (!pvt || Date.now() >= new Date(pvt).getTime()) {
          addToCartButtonContainer.enable();
        }
      }

      // Update the add to cart button text and icon
      if (newAddToCartButton) {
        morph(currentAddToCartButton, newAddToCartButton);
      }
    }

    if (this.refs.acceleratedCheckoutButtonContainer) {
      const pvtForAccelerated = this.dataset.preViewTime;
      const isPreView = pvtForAccelerated && Date.now() < new Date(pvtForAccelerated).getTime();
      if (event.detail.resource == null || event.detail.resource.available == false || isPreView) {
        this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
      } else {
        this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
      }
    }

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Update quantity selector's min/max/step attributes and cart quantity for the new variant
    const quantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
    const newQuantityInput = /** @type {HTMLInputElement | null} */ (
      event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
    );

    if (quantitySelector?.updateConstraints && newQuantityInput) {
      quantitySelector.updateConstraints(
        newQuantityInput.min,
        newQuantityInput.max || null,
        newQuantityInput.step
      );
    }

    // Check if quantity rules are appearing/disappearing (causes layout shift)
    const quantityRules = this.refs.quantityRules;
    const newQuantityRules = event.detail.data.html.querySelector('.quantity-rules');
    const isQuantityRulesChanging = !!quantityRules !== !!newQuantityRules;

    if (isQuantityRulesChanging && quantitySelector) {
      // Store quantity value before morphing entire container
      const currentQuantityValue = quantitySelector.getValue?.();

      const currentProductFormButtons = this.refs.productFormButtons;
      const newProductFormButtons = event.detail.data.html.querySelector('.product-form-buttons');

      if (currentProductFormButtons && newProductFormButtons) {
        morph(currentProductFormButtons, newProductFormButtons);

        // Get the NEW quantity selector after morphing and update its constraints
        const newQuantitySelector = /** @type {any} */ (this.querySelector('quantity-selector-component'));
        const newQuantityInputElement = /** @type {HTMLInputElement | null} */ (
          event.detail.data.html.querySelector('quantity-selector-component input[ref="quantityInput"]')
        );

        if (newQuantitySelector?.updateConstraints && newQuantityInputElement && currentQuantityValue) {
          // Temporarily set the old value so updateConstraints can snap it properly
          newQuantitySelector.setValue(currentQuantityValue);
          // updateConstraints will snap to valid increment if needed
          newQuantitySelector.updateConstraints(
            newQuantityInputElement.min,
            newQuantityInputElement.max || null,
            newQuantityInputElement.step
          );
        }
      }
    } else {
      // Update elements individually when layout isn't changing
      const quantityLabel = this.querySelector('.quantity-label');
      const newQuantityLabel = event.detail.data.html.querySelector('.quantity-label');
      this.#morphOrUpdateElement(quantityLabel, newQuantityLabel, quantitySelector);

      const addToCartButton = this.querySelector('[ref="addToCartButtonContainer"]');
      this.#morphOrUpdateElement(quantityRules, newQuantityRules, addToCartButton);
    }

    // Fetch and update cart quantity for the new variant
    await this.#fetchAndUpdateCartQuantity();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
